import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import DatePicker from '../../components/DatePicker';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const PregnancyCalculator = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState('lmp');
  const [lmp, setLmp] = useState(today);
  const [conception, setConception] = useState(today);
  const [cycleLen, setCycleLen] = useState(28);

  const result = useMemo(() => {
    let dueDate;
    if (mode === 'lmp') {
      const d = new Date(lmp);
      // Naegele's rule: LMP + 280 days, adjusted for cycle length
      d.setDate(d.getDate() + 280 + (cycleLen - 28));
      dueDate = d;
    } else {
      const d = new Date(conception);
      d.setDate(d.getDate() + 266);
      dueDate = d;
    }
    if (isNaN(dueDate)) return null;
    const now = new Date();
    const conceptionDate = new Date(dueDate);
    conceptionDate.setDate(conceptionDate.getDate() - 266);
    const daysSinceConception = Math.floor((now - conceptionDate) / (1000 * 60 * 60 * 24));
    const gestWeeks = Math.floor(daysSinceConception / 7) + 2; // gest age = LMP-based
    const gestDays = daysSinceConception % 7;
    const trimester = gestWeeks < 13 ? 1 : gestWeeks < 27 ? 2 : 3;
    const daysToDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    return { dueDate, gestWeeks: Math.max(gestWeeks, 0), gestDays: Math.max(gestDays, 0), trimester, daysToDue };
  }, [mode, lmp, conception, cycleLen]);

  return (
    <CalculatorLayout
      title="Pregnancy Calculator"
      subtitle="Estimate due date, current week, and trimester based on LMP or conception date."
      category="Fitness"
      description={<>
        <p>Pregnancy is typically 280 days (40 weeks) from the first day of the last menstrual period (LMP), or 266 days from conception. This calculator uses Naegele's rule.</p>
        <p><strong>Note:</strong> This is an estimate. Always consult your gynaecologist for accurate dates and medical advice.</p>
      </>}
      formulaNote="Due Date ≈ LMP + 280 days   •  1st trimester: 0–12 wk, 2nd: 13–26 wk, 3rd: 27–40 wk"
    >
      <Tabs value={mode} onValueChange={setMode} className="mb-5">
        <TabsList>
          <TabsTrigger value="lmp">By Last Period (LMP)</TabsTrigger>
          <TabsTrigger value="conception">By Conception Date</TabsTrigger>
        </TabsList>
        <TabsContent value="lmp" /><TabsContent value="conception" />
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          {mode === 'lmp' ? (
            <>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">First Day of Last Period</Label>
                <DatePicker value={lmp} onChange={setLmp} />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Average Cycle Length</Label>
                <input type="range" min="21" max="35" step="1" value={cycleLen} onChange={(e) => setCycleLen(+e.target.value)} className="w-full accent-saffron-500" />
                <div className="flex justify-between text-xs text-slate-500 mt-1"><span>21 days</span><span className="font-semibold text-bharat-navy">{cycleLen} days</span><span>35 days</span></div>
              </div>
            </>
          ) : (
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Date of Conception</Label>
              <DatePicker value={conception} onChange={setConception} />
            </div>
          )}
        </div>

        {result && (
          <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estimated Due Date</div>
            <div className="font-display text-2xl md:text-3xl font-bold text-bharat-navy display-font mt-1">
              {result.dueDate.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <Box label="Current Week" value={`${result.gestWeeks}w ${result.gestDays}d`} tone="saffron" />
              <Box label="Trimester" value={`${result.trimester}${['st','nd','rd'][result.trimester-1] || 'th'}`} tone="green" />
              <Box label="Days to Due Date" value={result.daysToDue > 0 ? `${result.daysToDue}` : 'Past due'} />
              <Box label="Conception Date" value={(() => { const d = new Date(result.dueDate); d.setDate(d.getDate() - 266); return d.toLocaleDateString('en-IN'); })()} />
            </div>
          </div>
        )}
      </div>
    </CalculatorLayout>
  );
};

const Box = ({ label, value, tone }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-3 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${c}`}>{value}</div>
    </div>
  );
};

export default PregnancyCalculator;
